import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SearchBar from '../components/SearchBar';
import CityDoctors from "../components/CityDoctors";
import HowItWorks from '../components/HowItWorks';
import Footer from '../components/Footer';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(null);
  
  // Update the page title
  useEffect(() => {
    document.title = "Home - TureMedicine";
  }, []);

  const handleSearch = (query) => {
    console.log('Searching for:', query);
  };

  const toggleAnswer = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  useEffect(() => {
    // Intersection observer for slide-in animation of the paragraph
    const paragraph = document.querySelector('.about-us-section p');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Add the class 'animate' to the paragraph when it enters the viewport
          paragraph.classList.add('animate');
        } else {
          // Optionally remove the animation class if it leaves the viewport
          paragraph.classList.remove('animate');
        }
      });
    }, { threshold: 0.1 });

    if (paragraph) {
      observer.observe(paragraph);
    }

    return () => {
      if (paragraph) {
        observer.unobserve(paragraph);
      }
    };
  }, []);

  return (
    <>
      <Navbar />
      <div className="home-container">
        <SearchBar onSearch={handleSearch} />
        <div className="hero-section">
          <h1>Experience Accessible Healthcare, Anytime, Anywhere</h1>
          <p>Bringing you top-notch healthcare from experienced doctors, right at your fingertips.</p>
          <div className="hero-buttons">
            <button onClick={() => navigate('/book-appointment')}>Book a Consultation</button>
            <button onClick={() => navigate('/search-doctor')}>Search for a Specialist</button>
          </div>
        </div>
        <div className="services-section">
          <h2>Our Services</h2>
          <p>We provide a comprehensive range of healthcare services for every need.</p>
          <div className="services-container">
            <div className="service-card">
              <h3>Comprehensive Symptom Analysis</h3>
              <p>Analyze your symptoms and get recommendations for care.</p>
            </div>
            <div className="service-card">
              <h3>Search for Doctors & Instant Booking</h3>
              <p>Find the right doctor and book a consultation in seconds.</p>
            </div>
            <div className="service-card">
              <h3>Seamless Video Consultations</h3>
              <p>Connect with doctors through secure and seamless video calls.</p>
            </div>
            <div className="service-card">
              <h3>Telehealthcare from Your City’s Doctors</h3>
              <p>Access trusted healthcare from doctors in your own city.</p>
            </div>
          </div>
        </div>
        <CityDoctors />
        <HowItWorks />
        
        <div className="about-us-section">
          <h2>About TureMedicine</h2>
          <p>
            TureMedicine is a telehealthcare service provider committed to offering accessible healthcare services to everyone, regardless of their location in India. Our goal is to bridge the gap in healthcare access by connecting patients with top doctors through convenient virtual consultations.
          </p>
        </div>

        <div className="testimonials-section">
          <h2>What Our Patients Say</h2>
          <p>&quot;Using TureMedicine has made accessing healthcare so much easier and faster!&quot;</p>
        </div>

        <div className="contact-us-container">
          <div className="contact-us-section">
            <h2>Get in Touch</h2>
            <p>Email: support@turemedicine.com</p>
            <p>Phone: +91-12345-67890</p>

            {/* Social Media Follow Buttons */}
            <div className="social-media home">
              <a href="https://www.facebook.com/shivendrajha.shivendra/" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-facebook-f"></i> Facebook
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-twitter"></i> Twitter
              </a>
              <a href="https://www.instagram.com/shivendra_jha__/" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-instagram"></i> Instagram
              </a>
              <a href="https://www.linkedin.com/in/shivendra-jha-102827215/" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-linkedin-in"></i> LinkedIn
              </a>
            </div>
          </div>
        </div>

        <div className="faq-section">
          <h2>Frequently Asked Questions</h2>
          
          {/* FAQ items */}
          {[
            {
              question: 'How do I book an appointment?',
              answer: `To book an appointment: 
              1. Log in to your TureMedicine account. 
              2. Use the search bar to find doctors based on specialization or city. 
              3. Select the doctor that fits your needs from the list of results. 
              4. Choose an available time slot and click "Book Appointment." 
              5. Confirm your booking and proceed with payment if required.`
            },
            {
              question: 'How do I know which doctor is right for me?',
              answer: `TureMedicine provides detailed doctor profiles, including: 
              - Specialization, experience, and qualifications. 
              - Patient reviews and ratings. 
              - Availability for consultations. 
              You can also use our symptom analysis tool to identify the type of doctor you need.`
            },
            {
              question: 'Is the video consultation secure?',
              answer: `Yes, all video consultations are conducted over a secure and encrypted platform to ensure patient-doctor confidentiality. Your data and conversations are fully protected as per healthcare data privacy standards.`
            },
            {
              question: 'Can I change or cancel my appointment?',
              answer: `Yes, you can change or cancel your appointment: 
              1. Go to your Profile page and navigate to your Appointment History. 
              2. Select the appointment you want to modify or cancel. 
              3. Follow the instructions to reschedule or cancel. 
              Note: Cancellation policies may vary depending on the doctor.`
            },
            {
              question: 'Are the consultations covered by insurance?',
              answer: `Currently, consultations may or may not be covered by insurance, depending on your policy and provider. Please check with your insurance provider to confirm coverage for telemedicine services.`
            },
            {
              question: 'How do I leave feedback after my consultation?',
              answer: `After your consultation: 
              1. Go to your Profile page and view your Appointment History. 
              2. Find the completed consultation and click "Leave Feedback." 
              3. Rate your experience and provide additional comments if desired. 
              Your feedback helps us improve our services and assist other patients in finding the right doctor.`
            }
          ].map((faq, index) => (
            <div className={`faq-item ${activeIndex === index ? 'active' : ''}`} key={index}>
              <button className="faq-question" onClick={() => toggleAnswer(index)}>
                <strong>{`Q${index + 1}: ${faq.question}`}</strong>
              </button>
              {activeIndex === index && (
                <p className="faq-answer">{faq.answer}</p>
              )}
            </div>
          ))}
        </div>
      </div>
      <Footer /> {/* Footer will be shown on every page */}
    </>
  );
};

export default Home;
